import React from 'react';

type PaymentHistorySkeletonProps = {
  variant?: 'desktop' | 'mobile';
  /** Number of table row placeholders to render */
  count?: number;
};

/** Single payment history table row skeleton */
const PaymentHistoryRowSkeleton: React.FC = () => (
  <tr className="relative">
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-200/70 rounded w-16 animate-pulse" />
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center gap-2">
        <div className="h-8 w-10 bg-gray-200/70 rounded-xl border border-[#E9E9E9]/70 animate-pulse flex-shrink-0" />
        <div className="h-4 bg-gray-200/70 rounded w-14 animate-pulse" />
        <div className="h-4 bg-gray-200/70 rounded w-20 animate-pulse hidden sm:block" />
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-200/70 rounded w-24 animate-pulse" />
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center gap-1">
        <div className="h-3 w-3 bg-gray-200/70 rounded-full animate-pulse" />
        <div className="h-4 bg-gray-200/70 rounded w-16 animate-pulse" />
      </div>
    </td>
  </tr>
);

/**
 * Aesthetic skeleton for Wallet payment history while user / payment history loads.
 */
const PaymentHistorySkeleton: React.FC<PaymentHistorySkeletonProps> = ({
  variant = 'desktop',
  count = 5,
}) => {
  const isDesktop = variant === 'desktop';

  return (
    <div
      className="bg-white rounded-lg shadow-sm relative overflow-hidden"
      role="status"
      aria-label="Loading payment history"
    >
      <div className="absolute inset-0 w-full h-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent z-10 pointer-events-none opacity-40" />

      <div className={`flex items-center justify-between border-b border-gray-200 ${isDesktop ? 'p-6' : 'p-4'}`}>
        <div className="h-4 bg-gray-200/70 rounded w-52 animate-pulse" />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <div className="h-3 bg-gray-200/70 rounded w-14 animate-pulse" />
              </th>
              <th className="px-6 py-3 text-left">
                <div className="h-3 bg-gray-200/70 rounded w-28 animate-pulse" />
              </th>
              <th className="px-6 py-3 text-left">
                <div className="h-3 bg-gray-200/70 rounded w-24 animate-pulse" />
              </th>
              <th className="px-6 py-3 text-left">
                <div className="h-3 bg-gray-200/70 rounded w-12 animate-pulse" />
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.from({ length: count }).map((_, index) => (
              <PaymentHistoryRowSkeleton key={index} />
            ))}
          </tbody>
        </table>
      </div>

      <span className="sr-only">Loading payment history...</span>
    </div>
  );
};

export default PaymentHistorySkeleton;
